<template>
  <v-card v-if="showDataPassAlert" class="datapass-banner">
    <v-card-text class="banner-card-text">
      <div class="banner-content">
        <div class="banner-text">
          <strong>Datapass Review Required:</strong>
          <span class="banner-description"> DataPass enables Owner/Operator data to be shared with authorized third-party recipients. Many of these authorizations were granted some time ago and may no longer reflect your current partnerships. Please review your DataPass authorizations and ensure data is only being shared with third parties you still actively work with.</span>
        </div>
      </div>

      <div class="banner-actions">
        <v-btn-tertiary @click="dismissDataPassBannerUntilNextLogin">
          Dismiss
        </v-btn-tertiary>

        <v-btn-secondary
          @click="handleMarkAsReviewed"
          :loading="isMarkingReviewed"
        >
          Mark as Reviewed
        </v-btn-secondary>

        <v-btn-primary
          @click="handleReviewInDatapass"
          :loading="isReviewingInDatapass"
        >
          Review in Datapass
        </v-btn-primary>
      </div>
    </v-card-text>
  </v-card>

  <v-snackbar
    v-model="snackbar.show"
    :timeout="-1"
    color="error"
    location="top"
    min-width="25rem"
    max-width="37.5rem"
    multi-line
  >
    <div class="text-body-1 py-2">
      {{ snackbar.message }}
    </div>
    <template #actions>
      <v-btn color="white" variant="text" @click="snackbar.show = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { shouldShowDataPassBanner as shouldShowBanner } from '../../helpers/data_pass_banner';
import { useRootStore } from '../../stores/root';
import getDataPassUser from './useDataPassUser';

/**
 * @typedef {object} UserGroup
 * @property {string} groupId - The unique identifier for the group
 * @property {string} name - The display name of the group
 */

const store = useRootStore();

const { updateUserSettings, getUserSettings } = store;
const rootStoreRefs = storeToRefs(store);
/** @type {import('vue').Ref<UserGroup[]>} */
const userGroups = rootStoreRefs.userGroups;

// Snackbar state
const snackbar = ref({
  show: false,
  message: '',
});

// Button loading states
const isMarkingReviewed = ref(false);
const isReviewingInDatapass = ref(false);
const lastAcknowledgeDate = ref(null);
const dismissedUntilLogin = ref(false);

onMounted(handleMounted);

/**
 * Initializes component state on mount by loading user settings and session storage.
 * Loads the last DataPass confirmation timestamp and checks if banner was dismissed.
 */
async function handleMounted() {
  try {
    const value = await getUserSettings('lastDataPassConfirmation');
    lastAcknowledgeDate.value = value?.timestamp;
  } catch (error) {
    console.error('Failed to load user settings:', error);
  }

  const dismissedValue = sessionStorage.getItem('datapassDismissedUntilLogin');
  if (dismissedValue && dismissedValue === 'true') {
    dismissedUntilLogin.value = true;
  } else {
    dismissedUntilLogin.value = false;
  }
}

/** @type {import('vue').ComputedRef<boolean>} */
const isOwnerOperator = computed(() => {
  return userGroups.value?.some(
    (group) => group.name?.toLowerCase() === 'owner operator',
  );
});

// Uses 'operator' instead of 'owner operator' because DataPass has its own role system separate from MyQ
/** @type {import('vue').ComputedRef<boolean>} */
const hasDataPassAccess = getDataPassUser({
  roles: ['operator', 'qsr_admin', 'mcd_admin'],
});

/** @type {import('vue').ComputedRef<boolean>} */
const shouldShowDataPassBanner = computed(() => {
  return shouldShowBanner(lastAcknowledgeDate.value, dismissedUntilLogin.value);
});

/** @type {import('vue').ComputedRef<boolean>} */
const showDataPassAlert = computed(() => {
  return (
    isOwnerOperator.value &&
    hasDataPassAccess.value &&
    shouldShowDataPassBanner.value
  );
});

/**
 * Dismiss the DataPass banner by setting the last acknowledgement date to the current date
 */
function dismissDataPassBanner() {
  lastAcknowledgeDate.value = new Date().toISOString();
}

/**
 * Hide the DataPass banner until the next login
 */
function dismissDataPassBannerUntilNextLogin() {
  dismissedUntilLogin.value = true;
  sessionStorage.setItem('datapassDismissedUntilLogin', 'true');
}

/**
 * Updates the user's DataPass confirmation timestamp
 * @returns {Promise<void>}
 * @throws {Error} if update fails
 */
async function updateDataPassConfirmation() {
  const now = new Date().toISOString();
  const success = await updateUserSettings('lastDataPassConfirmation', {
    timestamp: now,
  });
  if (!success) {
    throw new Error('Failed to update user settings');
  }
  dismissDataPassBanner();
}

/**
 * Handles the "Mark as Reviewed" button click
 */
async function handleMarkAsReviewed() {
  isMarkingReviewed.value = true;
  try {
    await updateDataPassConfirmation();
  } catch (error) {
    console.error('Failed to update user settings:', error);
    snackbar.value.message = 'Failed to save confirmation. Please try again.';
    snackbar.value.show = true;
  } finally {
    isMarkingReviewed.value = false;
  }
}

/**
 * Handles the "Review in Datapass" button click
 */
async function handleReviewInDatapass() {
  isReviewingInDatapass.value = true;
  try {
    await updateDataPassConfirmation();
    window.location.href = '/datapass/catalog?tab=authorized';
  } catch (error) {
    console.error('Failed to update user settings:', error);
    snackbar.value.message = 'Failed to save confirmation. Please try again.';
    snackbar.value.show = true;
  } finally {
    isReviewingInDatapass.value = false;
  }
}
</script>

<style scoped>
.datapass-banner {
  border-radius: 0.25rem;
  border: 1px solid rgb(var(--v-theme-border-info)) !important;
  background-color: rgb(var(--v-theme-surface-info-low)) !important;
  color: rgb(var(--v-theme-content-high));
  position: sticky;
  bottom: 1rem;
  left: 0;
  right: 0;
  z-index: 1000;
  margin: 1rem;
  box-shadow: none !important;
}

.banner-card-text {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem;
  font-size: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.banner-content {
  flex: 1;
  color: var(--v-theme-content-primary);
  line-height: 1.4;
  min-width: 300px;
}

.banner-actions {
  display: flex;
  gap: 1rem;
  box-shadow: none;
  flex-shrink: 0;
  align-items: flex-start;
  flex-direction: row;
}

@media (max-width: 768px) {
  .banner-card-text {
    flex-direction: column;
    align-items: stretch;
  }
  
  .banner-actions {
    justify-content: center;
    margin-top: 1rem;
  }
}
</style>
